import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import swal from "sweetalert";
import { twoFactorSettings } from "../../../../../services";
import SetupTwoFactorModal from "../../../../../components/SetupTwoFactorModal";
import "../settings.css";

/**
 * Security settings panel (redesign).
 *
 * Maps to the existing `Setting/Security/index.jsx`. We REUSE its real wiring:
 *   - 2FA preferences (SMS / Email) + master "enable 2FA access" switch are
 *     persisted through the existing `twoFactorSettings` service
 *     (`POST /auth/two-factor-settings`).
 *   - The reset / setup flow opens the existing `SetupTwoFactorModal`.
 *
 * The original component's same business rules are preserved:
 *   - At least one of SMS / Email must stay enabled (enforced on save).
 *   - Enabling SMS requires a phone number on file.
 *   - The master "Enable two-factor authentication" switch can't be turned on
 *     until 2FA has actually been verified (`twoFactorVerified`), and is gated
 *     to admins (`profile.user.role !== "staff"`) exactly as today.
 *
 * NEW design pieces with NO existing backend are rendered as clearly-disabled
 * "Coming soon" — never faked:
 *   - Authenticator app (TOTP)
 *   - Password change
 *   - Active sessions
 */
export default function SecurityPanel({ profile: profileProp, role }) {
  const { state } = useLocation();
  const profileFromStore = useSelector((s) => s?.profile?.profile);
  const profile = profileProp || profileFromStore;

  const userType = profile?.user?.type;
  const isStaff = userType ? userType === "staff" : role === "staff";
  // The original Security component gates the master switch on user.role
  const canManageMasterSwitch = profile?.user?.role !== "staff";

  const is2faVerified = !!profile?.user?.twoFactorVerified;

  const [saving, setSaving] = useState(false);
  const [twoFA, setTwoFA] = useState({ sms: false, email: false });
  const [is2FaAccess, setIs2FaAccess] = useState(false);

  const [
    setupModalOpened,
    { open: openSetupModal, close: closeSetupModal },
  ] = useDisclosure(false);

  // Seed 2FA preferences from the hydrated profile (same as existing component)
  useEffect(() => {
    setTwoFA({
      sms: !!profile?.user?.sms2FaEnabled,
      email: !!profile?.user?.email2FaEnabled,
    });
    setIs2FaAccess(!!profile?.user?.is2FaAccess);
  }, [profile]);

  // location.state.reset2fa auto-opens the setup modal (preserved behavior)
  useEffect(() => {
    if (state?.reset2fa) {
      openSetupModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.reset2fa]);

  const toggleMaster = () => {
    if (!canManageMasterSwitch) return;
    const next = !is2FaAccess;
    if (next && !is2faVerified) {
      swal(
        "Verification Required",
        "Please verify your 2FA first before enabling it.",
        "warning"
      );
      return;
    }
    setIs2FaAccess(next);
  };

  const toggleMethod = (key) => {
    if (key === "sms" && !profile?.user?.phone) {
      swal(
        "Phone Number Not Found",
        "Please set your phone number first from the reset button.",
        "error"
      );
      return;
    }
    setTwoFA((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!twoFA.sms && !twoFA.email) {
      swal(
        "Error",
        "At least one authentication method (SMS or Email) must be enabled.",
        "error"
      );
      return;
    }
    try {
      setSaving(true);
      const payload = {
        sms2FaEnabled: twoFA.sms,
        email2FaEnabled: twoFA.email,
        is2FaAccess,
      };
      const res = await twoFactorSettings(payload);
      swal("Success", `${res?.data?.message || "2FA settings updated."}`, "success");
    } catch (err) {
      console.error("Error updating 2FA settings:", err);
      swal("Error", "Failed to update 2FA settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="settings-panel active" data-panel="security">
        <div className="page-header">
          <div>
            <div className="page-title">Security</div>
            <div className="page-subtitle">
              Two-factor authentication, sessions, and password.
            </div>
          </div>
          <div className="page-actions">
            <button
              type="button"
              className="btn primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>

        {/* Unverified-2FA notice — driven by real profile state */}
        {!is2faVerified && (
          <div className="alert alert-warn">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Set up two-factor authentication to secure your account.
          </div>
        )}

        {/* ── Multi-factor authentication (REAL — wired to twoFactorSettings) ── */}
        <div className="card">
          <div className="card-section">
            <div className="card-title">Multi-factor authentication</div>
            <div className="card-subtitle">
              Add a second step at sign-in to keep patient data safe.
            </div>
          </div>

          {canManageMasterSwitch && (
            <div className="row">
              <div className="row-text">
                <div className="row-title">Enable two-factor authentication</div>
                <div className="row-subtitle">
                  Require an extra verification step during login.
                </div>
              </div>
              <button
                type="button"
                className={`switch${is2FaAccess ? " on" : ""}`}
                aria-pressed={is2FaAccess}
                onClick={toggleMaster}
              />
            </div>
          )}

          <div className="row">
            <div className="row-text">
              <div className="row-title">SMS</div>
              <div className="row-subtitle">
                One-time code by text to your verified phone.
                {profile?.user?.phone ? (
                  <>
                    {" "}
                    <strong style={{ color: "#404040" }}>
                      {profile.user.phone}
                    </strong>{" "}
                    <span
                      className={`badge ${
                        profile?.user?.isPhoneVerified
                          ? "badge-success"
                          : "badge-danger"
                      }`}
                    >
                      {profile?.user?.isPhoneVerified ? "Verified" : "Not verified"}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button type="button" className="btn sm" onClick={openSetupModal}>
                Reset
              </button>
              <button
                type="button"
                className={`switch${twoFA.sms ? " on" : ""}`}
                aria-pressed={twoFA.sms}
                onClick={() => toggleMethod("sms")}
              />
            </div>
          </div>

          <div className="row">
            <div className="row-text">
              <div className="row-title">Email</div>
              <div className="row-subtitle">
                One-time code by email.
                {profile?.user?.email ? (
                  <>
                    {" "}
                    <strong style={{ color: "#404040" }}>
                      {profile.user.email}
                    </strong>
                  </>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              className={`switch${twoFA.email ? " on" : ""}`}
              aria-pressed={twoFA.email}
              onClick={() => toggleMethod("email")}
            />
          </div>

          {/* Authenticator app — NO backend → clearly disabled "Coming soon" */}
          <div className="row disabled">
            <div className="row-text">
              <div className="row-title">
                Authenticator app{" "}
                <span className="badge badge-neutral">Coming soon</span>
              </div>
              <div className="row-subtitle">
                TOTP codes via Google Authenticator, 1Password, etc.
              </div>
            </div>
            <button type="button" className="switch" disabled />
          </div>

        </div>

        {/* ── Password — NO backend → clearly disabled "Coming soon" ── */}
        <div className="card">
          <div className="card-section">
            <div className="card-title">
              Password{" "}
              <span className="badge badge-neutral">Coming soon</span>
            </div>
            <div className="card-subtitle">
              Self-service password change isn't available here yet. Use the
              "Forgot password" flow on the sign-in screen to reset it.
            </div>
          </div>
          <div className="card-section">
            <div className="pwd-grid">
              <div>
                <label className="label">Current password</label>
                <input
                  className="input"
                  type="password"
                  placeholder="••••••••••"
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="label">New password</label>
                <input className="input" type="password" disabled readOnly />
              </div>
              <div>
                <label className="label">Confirm new password</label>
                <input className="input" type="password" disabled readOnly />
              </div>
            </div>
            <div className="field-hint" style={{ marginTop: 10 }}>
              Must be at least 12 characters with a mix of upper, lower, number,
              and symbol.
            </div>
          </div>
          <div className="card-footer">
            <button type="button" className="btn primary" disabled>
              Update password
            </button>
          </div>
        </div>

        {/* ── Active sessions — NO backend → clearly disabled "Coming soon" ── */}
        <div className="card">
          <div className="card-section">
            <div className="card-title">
              Active sessions{" "}
              <span className="badge badge-neutral">Coming soon</span>
            </div>
            <div className="card-subtitle">
              Device session management isn't available yet.
            </div>
          </div>
          <div className="card-section" style={{ opacity: 0.5, pointerEvents: "none" }}>
            <div className="session-row">
              <div>
                <div className="row-title">This device</div>
                <div className="row-subtitle">Currently signed in</div>
              </div>
              <span className="badge badge-success">Now</span>
            </div>
          </div>
          <div className="card-footer">
            <button type="button" className="btn danger-ghost" disabled>
              Sign out all other sessions
            </button>
          </div>
        </div>
      </div>

      {/* Reuse the existing setup/reset flow — real wiring, not rebuilt */}
      <SetupTwoFactorModal
        opened={setupModalOpened}
        close={closeSetupModal}
        profile={profile}
        isSettingPage={true}
      />
    </>
  );
}
